import type { AnnotationLabelT } from '../../../common/types/types';
import type { RelationshipStoreWithLabelDataT, RelationshipT } from '../../../modernization-common/relationships';
import { RELATIONSHIP_ROW_VARIANT, ROW_EXPAND_STATE, ROW_ID_SEPARATOR, TIMELINE_ROW_KIND } from '../constants/frame-timeline-v4';
import type { RelationshipRowDescriptorT, RelationshipRowVariantT, TimelineRowDescriptorT } from '../types/frame-timeline-v4';
export const buildRelationshipRowId = (sourceAnnotationId: string, relationshipId: string, variant: RelationshipRowVariantT): string => `${sourceAnnotationId}${ROW_ID_SEPARATOR}${variant === RELATIONSHIP_ROW_VARIANT.SAVED ? 'rel' : 'rel-draft'}${ROW_ID_SEPARATOR}${relationshipId}`;
export function buildRelationshipRow(relationship: RelationshipT, annotationIdToLabelMap: Map<string, AnnotationLabelT>, variant: RelationshipRowVariantT): RelationshipRowDescriptorT {
  const targetLabel = annotationIdToLabelMap.get(relationship.targetAnnotationId);
  const rawTitle = targetLabel?.title ?? relationship.targetAnnotationId;
  const title = rawTitle.charAt(0).toUpperCase() + rawTitle.slice(1);
  const targetName = `${title} [${relationship.targetAnnotationId}]`;
  return {
    kind: TIMELINE_ROW_KIND.RELATIONSHIP,
    id: buildRelationshipRowId(relationship.sourceAnnotationId, relationship.id, variant),
    annotationId: relationship.sourceAnnotationId,
    sourceAnnotationId: relationship.sourceAnnotationId,
    name: relationship.relationshipLabel.relationshipLabelName,
    color: targetLabel?.style.strokeColor,
    spans: relationship.frameRanges.map(range => ({
      start: range.startFrameIndex,
      end: range.endFrameIndex,
      value: targetName
    })),
    variant
  };
}
export function buildSavedRelationshipRows(relationshipStore: RelationshipStoreWithLabelDataT, annotationIdToLabelMap: Map<string, AnnotationLabelT>, sourceAnnotationId: string): RelationshipRowDescriptorT[] {
  return Object.values(relationshipStore).filter(relationship => relationship.sourceAnnotationId === sourceAnnotationId).map(relationship => buildRelationshipRow(relationship, annotationIdToLabelMap, RELATIONSHIP_ROW_VARIANT.SAVED));
}
function belongsToSource(row: TimelineRowDescriptorT, sourceAnnotationId: string): boolean {
  return row.id === sourceAnnotationId || row.id.startsWith(`${sourceAnnotationId}${ROW_ID_SEPARATOR}`);
}
function isStaleSavedRelationshipRow(row: TimelineRowDescriptorT, editingRelationshipId: string | undefined): boolean {
  return row.kind === TIMELINE_ROW_KIND.RELATIONSHIP && row.variant === RELATIONSHIP_ROW_VARIANT.SAVED && editingRelationshipId !== undefined && row.id === buildRelationshipRowId(row.sourceAnnotationId, editingRelationshipId, RELATIONSHIP_ROW_VARIANT.SAVED);
}
export function appendRelationshipDrafts(rows: TimelineRowDescriptorT[], draftRelationships: RelationshipT[], annotationIdToLabelMap: Map<string, AnnotationLabelT>, editingRelationshipId: string | undefined): TimelineRowDescriptorT[] {
  if (draftRelationships.length === 0) return rows;
  const draftRowsBySource = new Map<string, RelationshipRowDescriptorT[]>();
  for (const relationship of draftRelationships) {
    const list = draftRowsBySource.get(relationship.sourceAnnotationId) ?? [];
    list.push(buildRelationshipRow(relationship, annotationIdToLabelMap, RELATIONSHIP_ROW_VARIANT.DRAFT));
    draftRowsBySource.set(relationship.sourceAnnotationId, list);
  }
  const visibleRows = rows.filter(row => !isStaleSavedRelationshipRow(row, editingRelationshipId));
  return visibleRows.flatMap((row, index) => {
    const emittedRow: TimelineRowDescriptorT = row.kind === TIMELINE_ROW_KIND.ANNOTATION && draftRowsBySource.has(row.id) ? {
      ...row,
      expandState: ROW_EXPAND_STATE.EXPANDED
    } : row;
    const nextRow = visibleRows[index + 1];
    const draftRowsToAppend = [...draftRowsBySource.entries()].filter(([sourceAnnotationId]) => belongsToSource(row, sourceAnnotationId) && !(nextRow && belongsToSource(nextRow, sourceAnnotationId))).flatMap(([, draftRows]) => draftRows);
    return [emittedRow, ...draftRowsToAppend];
  });
}